import { useState, useCallback } from "react";
import { fetchFiles, FileEntry, FolderEntry } from "./fetchFiles";
import { FileStatusUpdate } from "./upload/useFileStatusUpdates";

/** Merge WebSocket update fields into an existing FileEntry */
export function applyFileStatusUpdate(
  file: FileEntry,
  update: FileStatusUpdate
): FileEntry {
  return {
    ...file,
    status: update.status,
    ...(update.blobId !== undefined && { fileId: update.blobId }),
    ...(update.fileName !== undefined && { fileName: update.fileName }),
    ...(update.fileType !== undefined && { fileType: update.fileType }),
    ...(update.uploadedAt !== undefined && {
      uploadedAt: update.uploadedAt || null,
    }),
    ...(update.documentDate !== undefined && {
      documentDate: update.documentDate || null,
    }),
    ...(update.isBlob !== undefined && { isBlob: update.isBlob }),
    ...(update.isPasswordProtected !== undefined && {
      isPasswordProtected: update.isPasswordProtected,
    }),
    ...(update.hasStoredPassword !== undefined && {
      hasStoredPassword: update.hasStoredPassword,
    }),
    ...(update.driveId !== undefined && { driveId: update.driveId || null }),
  };
}

export interface FolderExpansionState {
  files: FileEntry[];
  folders: FolderEntry[];
  isLoading: boolean;
  error: string | null;
}

function makeKey(filePath: string[], driveId: string | null): string {
  return JSON.stringify([driveId, ...filePath]);
}

/**
 * Hook managing inline folder expansion state for SearchResultsView.
 * Tracks which folders are expanded and their loaded children.
 */
export function useMockTreeExpansion(excludeBlobKinds?: string[]) {
  const [expansions, setExpansions] = useState<
    Map<string, FolderExpansionState>
  >(new Map());

  const isExpanded = useCallback(
    (filePath: string[], driveId: string | null): boolean => {
      return expansions.has(makeKey(filePath, driveId));
    },
    [expansions]
  );

  const getExpansion = useCallback(
    (
      filePath: string[],
      driveId: string | null
    ): FolderExpansionState | undefined => {
      return expansions.get(makeKey(filePath, driveId));
    },
    [expansions]
  );

  const toggleFolder = useCallback(
    (filePath: string[], driveId: string | null) => {
      const key = makeKey(filePath, driveId);

      if (expansions.has(key)) {
        // Collapse: remove this folder and all descendants
        setExpansions((prev) => {
          const next = new Map(prev);
          const prefix = JSON.stringify([driveId, ...filePath]);
          for (const k of next.keys()) {
            // Remove the folder itself and any key that starts with its path
            if (k === key) {
              next.delete(k);
            } else {
              // Check if k is a descendant: parse both keys and compare
              try {
                const parsed = JSON.parse(k) as (string | null)[];
                const parentParsed = JSON.parse(prefix) as (string | null)[];
                if (
                  parsed.length > parentParsed.length &&
                  parentParsed.every((seg, i) => parsed[i] === seg)
                ) {
                  next.delete(k);
                }
              } catch {
                // skip malformed keys
              }
            }
          }
          return next;
        });
        return;
      }

      // Expand: set loading state then fetch
      setExpansions((prev) => {
        const next = new Map(prev);
        next.set(key, {
          files: [],
          folders: [],
          isLoading: true,
          error: null,
        });
        return next;
      });

      fetchFiles({
        pathPrefix: filePath,
        maxDepth: 1,
        driveId: driveId ?? undefined,
        excludeBlobKinds,
      })
        .then((result) => {
          // Filter out the parent folder itself — the API includes it in
          // the response which would cause infinite recursion when we check
          // isFolderExpanded on children during recursive rendering.
          const parentPathKey = JSON.stringify(filePath);
          const childFolders = result.folders.filter(
            (f) => JSON.stringify(f.filePath) !== parentPathKey
          );
          setExpansions((prev) => {
            const next = new Map(prev);
            // Only update if still expanded (user might have collapsed while loading)
            if (next.has(key)) {
              next.set(key, {
                files: result.fileList,
                folders: childFolders,
                isLoading: false,
                error: null,
              });
            }
            return next;
          });
        })
        .catch((err) => {
          setExpansions((prev) => {
            const next = new Map(prev);
            if (next.has(key)) {
              next.set(key, {
                files: [],
                folders: [],
                isLoading: false,
                error:
                  err instanceof Error ? err.message : "Failed to load folder",
              });
            }
            return next;
          });
        });
    },
    [expansions, excludeBlobKinds]
  );

  const clearExpansions = useCallback(() => {
    setExpansions(new Map());
  }, []);

  /** Re-fetch all currently expanded (non-loading) folders, preserving expanded state */
  const refreshExpansions = useCallback(() => {
    setExpansions((prev) => {
      const next = new Map(prev);
      for (const [key, state] of prev) {
        if (state.isLoading) continue;
        // Parse the key back to [driveId, ...filePath]
        let parsed: (string | null)[];
        try {
          parsed = JSON.parse(key) as (string | null)[];
        } catch {
          continue;
        }
        const driveId = parsed[0];
        const filePath = parsed.slice(1) as string[];

        // Mark as loading while we re-fetch
        next.set(key, { ...state, isLoading: true });

        fetchFiles({
          pathPrefix: filePath,
          maxDepth: 1,
          driveId: driveId ?? undefined,
          excludeBlobKinds,
        })
          .then((result) => {
            const parentPathKey = JSON.stringify(filePath);
            const childFolders = result.folders.filter(
              (f) => JSON.stringify(f.filePath) !== parentPathKey
            );
            setExpansions((cur) => {
              const updated = new Map(cur);
              if (updated.has(key)) {
                updated.set(key, {
                  files: result.fileList,
                  folders: childFolders,
                  isLoading: false,
                  error: null,
                });
              }
              return updated;
            });
          })
          .catch((err) => {
            setExpansions((cur) => {
              const updated = new Map(cur);
              if (updated.has(key)) {
                updated.set(key, {
                  ...state,
                  isLoading: false,
                  error:
                    err instanceof Error
                      ? err.message
                      : "Failed to reload folder",
                });
              }
              return updated;
            });
          });
      }
      return next;
    });
  }, [excludeBlobKinds]);

  /** Update a file in any expanded folder by taskId, or add it if it belongs there */
  const updateFileByTaskId = useCallback((update: FileStatusUpdate) => {
    setExpansions((prev) => {
      let changed = false;
      const next = new Map(prev);

      for (const [key, state] of prev) {
        if (state.isLoading) continue;

        const idx = state.files.findIndex((f) => f.taskId === update.taskId);
        if (idx !== -1) {
          const updatedFiles = [...state.files];
          updatedFiles[idx] = applyFileStatusUpdate(updatedFiles[idx], update);
          next.set(key, { ...state, files: updatedFiles });
          changed = true;
        } else if (
          update.filesystemPath !== undefined &&
          update.fileName &&
          update.blobId
        ) {
          // Check if file belongs in this expanded folder
          let parsed: (string | null)[];
          try {
            parsed = JSON.parse(key) as (string | null)[];
          } catch {
            continue;
          }
          const folderDriveId = parsed[0];
          const folderPath = parsed.slice(1) as string[];

          if (
            JSON.stringify(update.filesystemPath) ===
              JSON.stringify(folderPath) &&
            (update.driveId || null) === folderDriveId
          ) {
            const newFile: FileEntry = {
              taskId: update.taskId,
              fileId: update.blobId,
              fileName: update.fileName,
              filePath: update.filesystemPath,
              fileType: update.fileType || "",
              status: update.status,
              isBlob: update.isBlob ?? true,
              isPasswordProtected: update.isPasswordProtected ?? false,
              hasStoredPassword: update.hasStoredPassword ?? false,
              driveId: update.driveId || null,
              uploadedAt: update.uploadedAt || null,
              documentDate: update.documentDate || null,
            };
            next.set(key, {
              ...state,
              files: [...state.files, newFile],
            });
            changed = true;
          }
        }
      }

      return changed ? next : prev;
    });
  }, []);

  return {
    isExpanded,
    getExpansion,
    toggleFolder,
    clearExpansions,
    refreshExpansions,
    updateFileByTaskId,
  };
}
